/**
 * useCalmAudio — owns the 528 Hz transformation-frequency sine oscillator.
 *
 * - Initialises a single AudioContext on first `start()`.
 * - Fades in to 0.15 gain over 0.5s, fades out over 0.3s on `stop()`.
 * - Cleans up on unmount.
 */
import { useCallback, useEffect, useRef } from 'react';

const CALM_FREQUENCY_HZ = 528;
const FADE_IN_SECONDS = 0.5;
const FADE_OUT_SECONDS = 0.3;
const PEAK_GAIN = 0.15;

export const useCalmAudio = () => {
  const ctxRef = useRef(null);
  const oscillatorRef = useRef(null);
  const gainRef = useRef(null);

  const start = useCallback(() => {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return;
    if (!ctxRef.current) ctxRef.current = new AudioContextClass();
    const ctx = ctxRef.current;
    if (ctx.state === 'suspended') ctx.resume();

    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(CALM_FREQUENCY_HZ, ctx.currentTime);

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0, ctx.currentTime);
    gain.gain.linearRampToValueAtTime(PEAK_GAIN, ctx.currentTime + FADE_IN_SECONDS);

    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();

    oscillatorRef.current = osc;
    gainRef.current = gain;
  }, []);

  const stop = useCallback(() => {
    const ctx = ctxRef.current;
    if (!gainRef.current || !ctx) return;
    gainRef.current.gain.linearRampToValueAtTime(0, ctx.currentTime + FADE_OUT_SECONDS);
    setTimeout(() => {
      try {
        oscillatorRef.current?.stop();
      } catch (_e) {
        // Already stopped — ignore.
      }
      oscillatorRef.current = null;
      gainRef.current = null;
    }, FADE_OUT_SECONDS * 1000 + 50);
  }, []);

  useEffect(() => {
    return () => {
      stop();
      if (ctxRef.current) {
        try { ctxRef.current.close(); } catch (_e) { /* noop */ }
      }
    };
  }, [stop]);

  return { start, stop };
};
